import { FormEvent, useEffect, useState } from 'react';
import type { InviteLookup, MeResponse } from '@kassa/shared';
import { api, ApiError } from '../api';

// Dəvətlə qeydiyyat (KT-84): link #qeydiyyat=KOD. Şirkət adı və e-poçt dəvətdən gəlir (dəyişmir — link e-poçtu təsdiqləyir).
// Sahib parol qoyur, ilk obyektin adını, ünvanını, ƏDV ödəyicisi olub-olmadığını yazır, şərtlərlə razılaşır.

const STATUS_TEXT: Record<Exclude<InviteLookup['status'], 'ok'>, string> = {
  invalid: 'Link keçərsizdir. Ola bilər ki, yenisi göndərilib — son məktubdakı linki açın.',
  used: 'Bu linklə qeydiyyat artıq tamamlanıb. Telefon, e-poçt və parolla daxil olun.',
  revoked: 'Dəvət ləğv olunub.',
  expired: 'Linkin vaxtı bitib.',
};

// Google-a gedəndə kod sessionStorage-da saxlanır (fraqment geri dönüşdə yoxdur). Xəta ilə qayıdanda qeydiyyat yenidən açılır;
// uğurda (və ya «hesab var») silinir. Yaddaş əlçatmazdırsa sadəcə giriş ekranı qalır.
const PENDING_KEY = 'kassa_invite_pending';
const BACK_REASONS: Record<string, string> = {
  google_email_mismatch: 'Google e-poçtu dəvətin e-poçtu ilə uyğun deyil. Dəvət gələn e-poçtun Google hesabını seçin və ya parol qoyun.',
  google_state: 'Google ilə qeydiyyatın vaxtı keçdi. Yenidən cəhd edin.',
  google_token: 'Google təsdiqi alınmadı. Yenidən cəhd edin.',
  google_unavailable: 'Google hazırda əlçatmazdır. Parol qoyaraq qeydiyyatdan keçin.',
  google_failed: 'Google ilə qeydiyyat alınmadı. Yenidən cəhd edin və ya parol qoyun.',
  access_denied: 'Google-da icazə verilmədi. Parol qoyaraq qeydiyyatdan keçə bilərsiniz.',
};

export function pendingInviteAfterGoogle(): { code: string; reason: string } | null {
  try {
    const code = sessionStorage.getItem(PENDING_KEY);
    if (!code) return null;
    const reason = new URLSearchParams(window.location.search).get('google');
    if (!reason || !Object.prototype.hasOwnProperty.call(BACK_REASONS, reason)) {
      sessionStorage.removeItem(PENDING_KEY);
      return null;
    }
    sessionStorage.removeItem(PENDING_KEY);
    window.history.replaceState(null, '', window.location.pathname);
    return { code, reason: BACK_REASONS[reason] };
  } catch {
    return null;
  }
}

export function Register({ code, notice, onDone, onCancel }: { code: string; notice?: string; onDone: (me: MeResponse) => void; onCancel: () => void }) {
  const [info, setInfo] = useState<InviteLookup | null>(null);
  const [error, setError] = useState<string | null>(notice ?? null);
  // Bu e-poçtla hesab artıq var (409): link sessiya vermir — öz parolu ilə daxil olmaq lazımdır
  const [exists, setExists] = useState(false);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [outletName, setOutletName] = useState('');
  const [address, setAddress] = useState('');
  const [vatPayer, setVatPayer] = useState<'' | 'yes' | 'no'>('');
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  // «Google ilə» (KT-84): yalnız server funksiyanı açıq elan edəndə
  const [googleOn, setGoogleOn] = useState(false);

  useEffect(() => {
    fetch('/api/auth/google/enabled', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((b: { enabled: boolean }) => setGoogleOn(b.enabled))
      .catch(() => setGoogleOn(false));
  }, []);

  useEffect(() => {
    api
      .inviteLookup(code)
      .then(setInfo)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Şəbəkə xətası. Səhifəni yeniləyin.'));
  }, [code]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== repeat) return setError('Parollar eyni deyil');
    if (!vatPayer) return setError('ƏDV ödəyicisi olub-olmadığını seçin');
    setBusy(true);
    setError(null);
    try {
      onDone(await api.inviteRegister({ code, password, outletName, address: address || undefined, vatPayer: vatPayer === 'yes', acceptTerms: true }));
    } catch (err) {
      setExists(err instanceof ApiError && err.code === 'account_exists');
      setError(err instanceof ApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  // Google ilə: obyekt, ƏDV və şərtlər eynidir, parol lazım deyil; Google e-poçtu dəvətin e-poçtu olmalıdır
  async function google() {
    if (!outletName.trim()) return setError('İlk obyektin adını yazın');
    if (!vatPayer) return setError('ƏDV ödəyicisi olub-olmadığını seçin');
    if (!terms) return setError('Şərtlər və məxfilik qaydası ilə razılaşın');
    setBusy(true);
    setError(null);
    try {
      try {
        sessionStorage.setItem(PENDING_KEY, code);
      } catch {
        // yaddaş yoxdur — xəta halında istifadəçi linki yenidən açır
      }
      const { url } = await api.inviteGoogle({ code, outletName, address: address || undefined, vatPayer: vatPayer === 'yes', acceptTerms: true });
      window.location.assign(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.');
      setBusy(false);
    }
  }

  if (!info) {
    return (
      <div className="page-center">
        {error ? <p className="bad" role="alert">{error}</p> : <p className="muted">Yüklənir…</p>}
      </div>
    );
  }

  if (info.status !== 'ok') {
    return (
      <div className="page-center">
        <div className="auth-form card">
          <div className="auth-heading">
            <h2>Qeydiyyat</h2>
          </div>
          <p className="bad" role="alert">
            {STATUS_TEXT[info.status]}
          </p>
          {info.adminEmail && info.status !== 'used' && (
            <a className="btn-secondary" href={`mailto:${info.adminEmail}?subject=${encodeURIComponent('Kassa: yeni qeydiyyat linki')}`}>
              Adminə yaz
            </a>
          )}
          <button type="button" className="link-btn" onClick={onCancel}>
            Girişə keç
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-center">
      <form className="auth-form card" onSubmit={submit} noValidate>
        <div className="auth-heading">
          <h2>Qeydiyyat — {info.companyName}</h2>
          <span className="muted">Salam, {info.ownerName}! Parol qoyun və ilk obyekti yazın.</span>
        </div>
        <label className="field">
          E-poçt
          <input value={info.email ?? ''} readOnly disabled autoComplete="username" />
        </label>
        <label className="field">
          Parol (ən azı 8 simvol)
          <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy} />
        </label>
        <label className="field">
          Parol (təkrar)
          <input type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} disabled={busy} />
        </label>
        <label className="field">
          İlk obyektin adı (mağaza, anbar)
          <input value={outletName} maxLength={120} onChange={(e) => setOutletName(e.target.value)} placeholder="Mərkəz" disabled={busy} />
        </label>
        <label className="field">
          Ünvan
          <input value={address} maxLength={300} onChange={(e) => setAddress(e.target.value)} disabled={busy} />
        </label>
        <fieldset className="fieldset">
          <legend>ƏDV ödəyicisisinizmi?</legend>
          <label className="check">
            <input type="radio" name="vat" checked={vatPayer === 'yes'} onChange={() => setVatPayer('yes')} disabled={busy} /> Bəli
          </label>
          <label className="check">
            <input type="radio" name="vat" checked={vatPayer === 'no'} onChange={() => setVatPayer('no')} disabled={busy} /> Xeyr
          </label>
        </fieldset>
        <label className="check">
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} disabled={busy} />
          <span>
            <a href="/terms.html" target="_blank" rel="noreferrer">Şərtlər</a> və <a href="/privacy.html" target="_blank" rel="noreferrer">məxfilik qaydası</a> ilə razıyam
          </span>
        </label>
        {error && (
          <p className="bad" role="alert">
            {error}
          </p>
        )}
        {exists && (
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Girişə keç
          </button>
        )}
        <button type="submit" className="btn-primary" disabled={busy || password.length < 8 || !repeat || !outletName.trim() || !terms}>
          {busy ? 'Yaradılır…' : 'Qeydiyyatı tamamla'}
        </button>
        {googleOn && (
          <>
            <p className="muted small">və ya parolsuz — {info.email} Google hesabı ilə:</p>
            <button type="button" className="btn-secondary" onClick={() => void google()} disabled={busy || !outletName.trim() || !terms}>
              Google ilə qeydiyyat
            </button>
          </>
        )}
      </form>
    </div>
  );
}
